import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Column } from 'react-table';
import { useAppSelector, useAppDispatch } from '../../hooks/storeHooks';
import { RootState } from '../../store';
import {
  fetchWellCompletionData,
  WellCompletionData,
  fetchCompletionWarningsData,
  fetchCombinedCompletion,
  fetchLiftTypesEnum,
  fetchManufacturerControllers,
  fetchManufacturersWithControllers,
  fetchManufacturers,
  fetchPumpManufacturers,
} from '../../services/well-completions/WellCompletionsService';
import DataTableGrid from '../../components/common/DataTableGrid';
import ExportTable from '../../components/common/export-table/ExportTable';
import Loader from '../common/Loader/Loader';
import { ReactComponent as NotepadIcon } from '../../images/note-pad.svg';
import { ReactComponent as TrashIcon } from '../../images/trash.svg';
import { formatDateCell, formatStatusCell } from '../../utilities/tableCellHelpers';
import { formatDate } from '../../utilities/dateHelpers';
import './WellCompletions.scss';
import {
  getMopFromIndustryApplicationId,
  getMOPforCompletionWarnings,
  createTableSearchFilter,
} from '../../utilities/completionsUtils';
import CreateCompletionsModal from './create-Completions/CreateCompletionsModal';
import Modal from '../common/Modal/Modal';
import shutdownIcon from '../../images/shutdown.svg';
import { deleteESPCompletions, deleteGLCompletions } from '../../services/well-completions/WellCompletionsService';
import { useToastService } from '../../components/toast/ToastService';

const WellCompletionsTableWidget = () => {
  const dispatch = useAppDispatch();
  const { showSuccessToast, showErrorToast } = useToastService();

  const selectedAssetId = useAppSelector((state: RootState) => state.assetGroups?.selectedAssetId);
  const wellCompletionsState = useAppSelector((state: RootState) => state.WellCompletions);
  const wellId = useAppSelector((state: RootState) => state.WellTestIdSlice?.data?.WellId);
  const wellName = useAppSelector((state: RootState) => state.WellTestIdSlice?.data?.Name);
  const industryApplicationId = useAppSelector((state: RootState) => state.AssetAnalysis.industryApplicationId);
  const wellCompletionsPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const selectedLiftType = useAppSelector((state: any) => state.CreateCompletions.selectedLiftType);
  const deleteGLState = useAppSelector((state: RootState) => state.WellCompletions?.deleteGLCompletions);
  const deleteESPState = useAppSelector((state: RootState) => state.WellCompletions?.deleteESPCompletions);
  const deleteLoading = deleteGLState?.loading || false || deleteESPState?.loading || false;
  const deleteError = deleteGLState?.error || deleteESPState?.error;
  const previousDeleteLoadingRef = useRef(false);

  const completionData = wellCompletionsState.currentCompletion.data || [];
  const loading = wellCompletionsState.currentCompletion.loading;

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [pendingDeleteLiftType, setPendingDeleteLiftType] = useState<string | null>(null);
  const rPermission = { canExport: true };

  const mop = useMemo(() => getMopFromIndustryApplicationId(industryApplicationId ?? 0), [industryApplicationId]);

  const wellCompletionsData = useMemo(
    () => wellCompletionsPageData?.find((obj: any) => obj?.key === `${mop}-completions-table`) || null,
    [wellCompletionsPageData, mop],
  );

  const ColumnHeaders = useMemo(
    () => wellCompletionsData?.widgetProperty?.widgetProperties?.columns || [],
    [wellCompletionsData],
  );

  const tableData = useMemo(() => {
    if (!completionData || completionData.length === 0) return [];

    return [...completionData].sort((a, b) => {
      const installDateA = new Date(a.InstallDate).getTime() || 0;
      const installDateB = new Date(b.InstallDate).getTime() || 0;
      if (installDateB !== installDateA) {
        return installDateB - installDateA;
      }

      const installNumA = Number(a.InstallNumber) || 0;
      const installNumB = Number(b.InstallNumber) || 0;
      return installNumB - installNumA;
    });
  }, [completionData]);

  // Helper function to get equipment display value
  const getEquipmentDisplayValue = useCallback((row: any, columnId: string) => {
    const liftType = row?.LiftTypeDescription || row?.original?.LiftTypeDescription;

    if (liftType === 'ESP') {
      return row?.EspEquipment?.DisplayEquipment || row?.original?.EspEquipment?.DisplayEquipment || '';
    } else if (liftType === 'GAPL') {
      return row?.GAPLEquipment?.DisplayEquipment || row?.original?.GAPLEquipment?.DisplayEquipment || '';
    } else if (liftType === 'GL') {
      return row?.GLEquipment?.DisplayEquipment || row?.original?.GLEquipment?.DisplayEquipment || '';
    } else {
      if (columnId === 'EspEquipment') {
        return row?.EspEquipment?.DisplayEquipment || row?.original?.EspEquipment?.DisplayEquipment || '';
      } else if (columnId === 'GLEquipment') {
        return row?.GLEquipment?.DisplayEquipment || row?.original?.GLEquipment?.DisplayEquipment || '';
      } else if (columnId === 'GAPLEquipment') {
        return row?.GAPLEquipment?.DisplayEquipment || row?.original?.GAPLEquipment?.DisplayEquipment || '';
      }
    }
    return '';
  }, []);

  const searchableColumns = useMemo(() => ColumnHeaders.filter((col: any) => col.selected), [ColumnHeaders]);

  const searchFilter = useMemo(
    () =>
      createTableSearchFilter({
        searchableColumns,
        getEquipmentDisplayValue,
        dateColumns: new Set(['InstallDate', 'StartDate', 'FailDate', 'PullDate']),
        statusColumns: new Set(['Status', 'Well Status']),
        equipmentColumns: new Set(['EspEquipment', 'GLEquipment', 'GAPLEquipment']),
      }),
    [searchableColumns, getEquipmentDisplayValue],
  );

  // Export XLS accessor
  const createExportAccessor = useCallback(
    (columnId: string, accessor: any) => {
      return (row: any) => {
        if (columnId === 'Status' || columnId === 'Well Status') {
          return row.original?.Status || row.original?.[accessor as string] || '';
        }

        if (['EspEquipment', 'GLEquipment', 'GAPLEquipment'].includes(columnId)) {
          return getEquipmentDisplayValue(row, columnId);
        }

        if (['InstallDate', 'StartDate', 'FailDate', 'PullDate'].includes(columnId)) {
          const raw = row.original?.[accessor as string];
          if (!raw) return '';
          const parsed = new Date(raw);
          if (!isNaN(parsed.getTime())) {
            return formatDate(parsed);
          }
          return String(raw);
        }

        if (typeof accessor === 'string') {
          const value = row.original?.[accessor];
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return JSON.stringify(value);
          return String(value);
        }

        return '';
      };
    },
    [getEquipmentDisplayValue],
  );

  // column configuration
  const createColumnConfig = useCallback(
    (col: any) => {
      const baseConfig = {
        Header: col.label,
        accessor: col.key,
        id: col.key,
      };

      if (['InstallDate', 'StartDate', 'FailDate', 'PullDate'].includes(col.key)) {
        return { ...baseConfig, Cell: formatDateCell };
      }

      if (col.key === 'Status' || col.key === 'Well Status') {
        return {
          ...baseConfig,
          accessor: 'Status',
          Cell: formatStatusCell,
        };
      }

      if (['EspEquipment', 'GLEquipment', 'GAPLEquipment'].includes(col.key)) {
        return {
          ...baseConfig,
          Cell: (props: any) => {
            const value = getEquipmentDisplayValue(props.row?.original, col.key) || '-';
            return <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>{value}</span>;
          },
        };
      }

      return {
        ...baseConfig,
        Cell: (props: any) => {
          const value = props.value;
          if (value === null || value === undefined) return <span>-</span>;
          if (typeof value === 'object') return <span>{JSON.stringify(value)}</span>;
          return <span>{value}</span>;
        },
      };
    },
    [getEquipmentDisplayValue],
  );

  const handleEditCompletion = useCallback(
    (completionData: WellCompletionData) => {
      if (wellId) {
        dispatch(fetchCombinedCompletion({ wellId, installationId: completionData.InstallationId }));
        setIsEditMode(true);
        setIsCreateModalOpen(true);
      }
    },
    [wellId, dispatch],
  );

  const handleCreateCompletions = useCallback(() => {
    if (wellId) {
      const newMopValue = parseInt(selectedLiftType) || 2;
      dispatch(fetchCombinedCompletion({ wellId, installationId: 0, originalMop: 2, newMop: newMopValue }));
    }
    setIsEditMode(false);
    setIsCreateModalOpen(true);
  }, [wellId, selectedLiftType, dispatch]);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setIsEditMode(false);
  }, []);

  const handleSaveCompletion = useCallback(() => {
    if (wellId) {
      dispatch(fetchWellCompletionData(wellId));
    }
  }, [wellId, dispatch]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const GL_LIFT_TYPES = new Set(['GL', 'HPGL', 'GAPL']);

  const getDeleteThunk = (liftType: string) => {
    return GL_LIFT_TYPES.has(liftType) ? deleteGLCompletions : deleteESPCompletions;
  };

  const handleDeleteCompletion = useCallback((completionData: WellCompletionData) => {
    setPendingDeleteId(completionData.InstallationId);
    setPendingDeleteLiftType(completionData.LiftTypeDescription);
    setShowConfirmModal(true);
  }, []);

  const confirmDeleteCompletion = useCallback(() => {
    if (pendingDeleteId !== null && pendingDeleteLiftType) {
      const thunk = getDeleteThunk(pendingDeleteLiftType);
      dispatch(thunk(pendingDeleteId));
      setShowConfirmModal(false);
      setPendingDeleteId(null);
      setPendingDeleteLiftType(null);
    }
  }, [dispatch, pendingDeleteId, pendingDeleteLiftType]);

  // Toast and reload table after delete
  useEffect(() => {
    if (previousDeleteLoadingRef.current && !deleteLoading) {
      if (deleteError) {
        showErrorToast('Delete failed', deleteError);
      } else {
        showSuccessToast('Completion has been successfully deleted.');
        if (wellId) {
          dispatch(fetchWellCompletionData(wellId));
        }
      }
    }
    previousDeleteLoadingRef.current = deleteLoading;
  }, [deleteLoading, deleteError, showErrorToast, showSuccessToast, wellId, dispatch]);

  // Build columns dynamically from ColumnHeaders
  const dynamicColumns: Column<WellCompletionData>[] = useMemo(() => {
    const allowedLiftTypes = ['ESP', 'GL', 'HPGL', 'GAPL'];

    const actionColumn = {
      Header: '',
      id: 'completionActions',
      width: 80,
      Cell: ({ row }: { row: { original: WellCompletionData } }) => {
        const liftType = row.original.LiftTypeDescription;
        const showActions = allowedLiftTypes.includes(liftType);

        return (
          <div className='tdt-action-cell'>
            {showActions && (
              <>
                <NotepadIcon onClick={() => handleEditCompletion(row.original)} />
                <TrashIcon onClick={() => handleDeleteCompletion(row.original)} style={{ cursor: 'pointer' }} />
              </>
            )}
          </div>
        );
      },
    };

    const dataColumns = ColumnHeaders.filter((col: any) => col.selected).map(createColumnConfig);
    return [actionColumn, ...dataColumns];
  }, [ColumnHeaders, handleEditCompletion, createColumnConfig, handleDeleteCompletion]);

  const tableRowsForExport = useMemo(() => {
    const dataToExport = searchTerm ? searchFilter(tableData, searchTerm) : tableData;
    return dataToExport.map((item: WellCompletionData) => ({ original: item }));
  }, [tableData, searchTerm, searchFilter]);

  // Generate export columns
  const exportColumns = useMemo(() => {
    return dynamicColumns
      .filter((column) => column.id !== 'completionActions')
      .map((column) => {
        const columnId = column.id || '';
        const accessor = column.accessor;

        return {
          key: columnId,
          header: typeof column.Header === 'string' ? column.Header : '',
          accessor: createExportAccessor(columnId, accessor),
        };
      });
  }, [dynamicColumns, createExportAccessor]);

  useEffect(() => {
    dispatch(fetchLiftTypesEnum());
    dispatch(fetchManufacturers());
    dispatch(fetchManufacturerControllers());
    dispatch(fetchManufacturersWithControllers());
    dispatch(fetchPumpManufacturers());
  }, [dispatch]);

  useEffect(() => {
    if (wellId) {
      dispatch(fetchWellCompletionData(wellId));
    }
  }, [wellId, dispatch]);

  useEffect(() => {
    if (wellId && industryApplicationId) {
      const mopForWarnings = getMOPforCompletionWarnings(industryApplicationId);
      dispatch(fetchCompletionWarningsData({ wellId, MOP: mopForWarnings }));
    }
  }, [dispatch, wellId, industryApplicationId]);

  useEffect(() => {
    if (wellId && isCreateModalOpen && !isEditMode && selectedLiftType) {
      const newMopValue = parseInt(selectedLiftType) || 2;
      dispatch(fetchCombinedCompletion({ wellId, installationId: 0, originalMop: 2, newMop: newMopValue }));
    }
  }, [dispatch, wellId, isCreateModalOpen, isEditMode, selectedLiftType]);

  return (
    <>
      <div className='completions-main-container table-wrap'>
        <header className='cust-table-header'>
          <div
            className='cust-table-header-wrap'
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <h1 className='table-header-title'>{wellName ? `${wellName}` : ''} - Completions history</h1>
            <button className='btn-prime' onClick={handleCreateCompletions} disabled={loading || !wellId}>
              <span className='ico-add' />
              <span className='text-button'>Create completions</span>
            </button>
          </div>
        </header>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '16px 20px',
          }}
        >
          <div className='recommended-export-section'>
            <ExportTable
              data={tableRowsForExport}
              fileName={`Well-Completions-${selectedAssetId}-${formatDate(new Date()).replace(/,/g, '').replace(/ /g, '-')}`}
              sheetName='Well Completions'
              columns={exportColumns}
              disabled={loading || !tableRowsForExport || tableRowsForExport.length === 0}
              rPermission={rPermission}
            />
          </div>

          <div className='flex pt-[7px] pr-[12px] pb-[6px] pl-[12px] items-center self-stretch shrink-0 flex-nowrap bg-[#001023] rounded-[8px] border border-solid border-[#4a5463] relative shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] search-input w-[20%]'>
            <span className='ico-search' />
            <div className='relative flex w-full items-center'>
              <input
                type='text'
                placeholder='Search completions...'
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ border: 'none', background: 'transparent', outline: 'none' }}
                className="flex w-full h-[24px] pl-[8px] pr-[24px] font-['Mulish'] text-[16px] font-normal leading-[24px] text-[#b8c5cc] bg-transparent border-none"
              />

              {searchTerm && (
                <button
                  className='absolute right-[4px] text-[#b8c5cc] hover:text-white cursor-pointer text-[16px] leading-none bg-transparent border-none p-0 m-0'
                  onClick={() => setSearchTerm('')}
                  type='button'
                  aria-label='Clear search'
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {loading && tableData.length === 0 && (
          <div className='well-completions-loader'>
            <Loader />
          </div>
        )}
        <DataTableGrid
          data={tableData}
          columns={dynamicColumns}
          searchTerm={searchTerm}
          customSearchFilter={searchFilter}
        />
      </div>
      <CreateCompletionsModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        title={isEditMode ? 'Edit completions' : 'Create completions'}
        onSave={handleSaveCompletion}
        isEditMode={isEditMode}
      />

      <Modal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        callbackOnConfirm={confirmDeleteCompletion}
        title='Confirm delete'
        message='Are you sure you want to delete this completion?'
        buttonText='Yes'
        Icon={shutdownIcon}
        hideCancelButton={false}
        buttonColor='red'
      />
    </>
  );
};

export default WellCompletionsTableWidget;
